<template>
    <div>
        <DsModalHeader fullscreen>
            <template #leading>
                <DsIconButton
                    :dense="false"
                    :disabled="false"
                    :floating="false"
                    name="x"
                    @click="close"
                />
            </template>

            <template #title>
                <DsModalHeaderTitle :title="modalTitle" />
            </template>

            <template #trailing>
                <DsFilledButton
                    v-if="isValidAccount && !showEndpointError"
                    data-qa="text-voice-then-save-button"
                    @click="clickedSaving"
                >
                    <template #default>
                        {{ $t('funnel.edit.done') }}
                    </template>
                </DsFilledButton>
            </template>
        </DsModalHeader>

        <div v-if="loading" class="ktv-then-spinner">
            <DsSpinner :size="200" />
        </div>

        <div v-else class="ktv-then">
            <div class="ktv-then-content">
                <DsInlineAlert
                    v-if="showEndpointError"
                    leading-icon
                    data-qa="ktv-error-alert"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span class="ktv-disabled-number">
                        {{ endpointErrorMessages }}
                    </span>
                    <DsFilledButton
                        data-qa="ktv-verification-status-action-button"
                        class="go-back-button"
                        @click="handleSettingButtonClick"
                    >
                        {{ $t('ktvThen.KtvEndPointErrors.errorSettingsButton') }}
                    </DsFilledButton>
                </DsInlineAlert>
                <DsInlineAlert
                    v-if="!isValidAccount && !showEndpointError"
                    leading-icon
                    data-qa="ktv-disabled-alert"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span class="ktv-disabled-number"> {{ $t('ktvThen.inactiveAccount.messaging') }}
                        <span class="disabled-number">{{ $t('ktvThen.inactiveAccount.unavailable') }} </span>
                        {{ $t('ktvThen.inactiveAccount.text') }}
                    </span>

                    <span class="ktv-disabled-number">
                        {{ $t('ktvThen.inactiveAccount.please') }}
                        <DsLink
                            as="a"
                            class="support-link"
                            external
                            :href="$options.SUPPORT_URL"
                        >
                            {{ $t('ktvThen.inactiveAccount.support') }}
                        </DsLink>
                    </span>

                    <DsFilledButton
                        gray
                        class="go-back-button"
                        @click="close"
                    >
                        {{ $t('ktvThen.inactiveAccount.goBack') }}
                    </DsFilledButton>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !isPhoneTypesValid"
                    leading-icon
                    data-qa="ktv-phone-type-invalid"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.phoneType') }}</span>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !hasMessage"
                    leading-icon
                    data-qa="ktv-message-empty"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.emptyMessage') }}</span>
                    <span>{{ $t('ktvThen.KtvInlineAlerts.emptyMessagePlease') }}</span>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !permissionValue"
                    leading-icon
                    data-qa="ktv-no-permission"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.permission') }}</span>
                </DsInlineAlert>

                <KtvPhoneNumber
                    :phone-number="formattedPhoneNumber"
                    class="ktv-card"
                    :class="{'error': showError}"
                    data-qa="ktv-phone-number"
                />

                <KtvPhoneType
                    :phone-types="automationMessage.phoneTypes"
                    :show-error="showError && !isPhoneTypesValid"
                    :saving="saving"
                    :read-only="!isValidAccount || showEndpointError"
                    class="ktv-card"
                    data-qa="ktv-phone-type"
                    @input="updatePhoneTypes"
                />

                <KtvEditMessage
                    :message="automationMessage.messageContent"
                    :saving="saving"
                    :content-editable="isValidAccount && !showEndpointError"
                    :show-error="showError && !isMessageValid"
                    class="ktv-card"
                    data-qa="ktv-edit-message"
                    @message-updated="updateBody"
                />

                <KtvPermission
                    data-qa="ktv-permission"
                    :disabled="!isValidAccount || showEndpointError"
                    :permission-value="permissionValue"
                    class="ktv-card"
                    @input="(permission) => { permissionValue = permission }"
                />

                <SmsBroadcastFooter class="ktv-footer" />
            </div>

            <SmsPreview
                class="ktv-then-preview"
                :account="account"
                :message="automationMessage.messageContent"
                :show-opt-in-button="false"
                show-today-date
            />
        </div>
    </div>
</template>

<script>

import KtvPhoneNumber from './components/KtvPhoneNumber.vue';
import KtvPhoneType from './components/KtvPhoneType.vue';
import KtvPermission from './components/KtvPermission.vue';
import KtvEditMessage from './components/KtvEditMessage.vue';
import SmsPreview from '@/components/automatedSms/SmsPreview.vue';
import SmsBroadcastFooter from '@/components/broadcast/sms/SmsBroadcastFooter.vue';
import { containsSupportedCharacters } from '@/components/automatedSms/automatedSms.util';
import {
    getAccountPhoneNumberInfo,
    createAutomationMessage,
    getAutomationMessageInfo,
    updateAutomationMessage,
} from './textVoiceThen.api';
import { utils } from '../../../utils/legacy-utils';

export const SUPPORT_URL = 'https://learn.thryv.com/hc/en-us/articles/40931563866381-Thryv-Text-Registration-and-Verification';
export const TEXT_MESSAGE_VERIFICATION_SETTINGS_ULTIMATE = '/app/miscSetting/itemWrapper?systemId=nav.marketing&settingModuleName=Marketing&settingTabName=text.verification.settings.label';
export const TEXT_MESSAGE_VERIFICATION_SETTINGS_SPA = '/settings/text-and-voice';

export default {
    SUPPORT_URL,

    components: {
        SmsBroadcastFooter,
        SmsPreview,
        KtvPhoneNumber,
        KtvPhoneType,
        KtvPermission,
        KtvEditMessage,
    },
    props: {
        cell: {
            type: Object,
            default: () => ({}),
        },
        modalTitle: {
            type: String,
            default: '',
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            account: {
                id: 0,
                status: '',
                phoneNumber: '',
                phoneNumberId: 0,
                phoneNumberStatus: '',
                source: '',
                verificationStatus: '',
                optInMessageIntro: '',
                optInMessage: '',
                optInConfirmationMessage: '',
            },
            automationMessage: {
                id: this.cell?.value?.externalId ?? 0,
                messageContent: '',
                phoneTypes: [],
            },
            permissionValue: false,
            showError: false,
            saving: false,
            loading: true,
            showEndpointError: false,
            endpointErrorMessages: '',
        };
    },

    computed: {
        isPhoneTypesValid() {
            return !!this.automationMessage.phoneTypes.length;
        },

        isMessageTooLong() {
            return this.automationMessage.messageContent?.length > this.maxCharacterCount;
        },

        hasMessage() {
            return !!this.automationMessage.messageContent?.length;
        },

        isMessageValid() {
            return !!this.automationMessage.messageContent?.length && !this.isMessageTooLong && this.messageContainsValidCharacters;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.automationMessage.messageContent);
        },

        formattedPhoneNumber() {
            let phone = this.account?.phoneNumber;

            if (!phone) {
                return '';
            }

            let numbers = phone.replace(/\D/g, '');

            if (numbers.startsWith('1')) {
                numbers = numbers.substring(1);
            }

            const char = { 0: '(', 3: ') ', 6: '-' };

            phone = '';

            for (let i = 0; i < numbers.length; i++) {
                phone += (char[i] || '') + numbers[i];
            }

            return phone;
        },

        isValidAccount() {
            let allowedPhoneNumberStatuses = [];

            if (this.account.id !== 0 && this.account.status === 'ACTIVE'
                && !!this.account.phoneNumber && this.account.phoneNumberStatus === 'ACTIVE') {
                if (this.account.source === 'KMN' || this.account.source === 'KBL') {
                    allowedPhoneNumberStatuses = ['APPROVED', 'PENDING'];
                } else if (this.account.source === 'KTV') {
                    allowedPhoneNumberStatuses = ['APPROVED'];
                }
            }

            return allowedPhoneNumberStatuses.includes(this.account.verificationStatus);
        },

        isConfigurationValid() {
            return this.isPhoneTypesValid && this.isMessageValid && this.permissionValue;
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        spaBaseUrl() {
            return this.appInfo?.spaBaseUrl;
        },
    },

    async mounted () {
        try {
            this.showEndpointError = false;
            this.endpointErrorMessages = '';

            const { data } = await getAccountPhoneNumberInfo();

            if (data) {
                this.account.id = data.accountId;
                this.account.status = data.accountStatus;
                this.account.phoneNumber = data.phoneNumber;
                this.account.phoneNumberId = data.phoneNumberId;
                this.account.phoneNumberStatus = data.phoneNumberStatus;
                this.account.source = data.source;
                this.account.verificationStatus = data.verificationStatus;
            }
        } catch (error) {
            this.showEndpointError = true;

            if (error.response?.status === 503) {
                this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorFetchingAccount')} ${this.$t('ktvThen.KtvEndPointErrors.serviceUnavailable')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
            } else {
                this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorFetchingAccount')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
            }

            console.error(this.endpointErrorMessages, error);
        }

        if (this.isValidAccount) {
            try {
                if (this.automationMessage.id) {
                    const { data } = await getAutomationMessageInfo(this.automationMessage.id);

                    this.automationMessage.messageContent = data?.body || '';
                    this.automationMessage.phoneTypes = data?.phoneTypes || [];
                } else {
                    this.automationMessage.phoneTypes = ['Mobile', 'Work', 'Home', 'Other'];
                }
            } catch (error) {
                this.showEndpointError = true;

                if (error.response?.status === 503) {
                    this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorFetchingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.serviceUnavailable')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                    // Fail silently and let the user see the error state in the UI
                } else {
                    this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorFetchingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                }

                console.error(this.endpointErrorMessages, error);
            }
        }

        this.loading = false;
    },

    methods: {
        updatePhoneTypes(phoneTypes) {
            this.automationMessage.phoneTypes = phoneTypes;

            if (this.isPhoneTypesValid && this.showError) {
                this.showError = false;
            }
        },

        updateBody(message) {
            this.automationMessage.messageContent = message;

            if (this.isMessageValid && this.showError) {
                this.showError = false;
            }
        },

        async saveConfiguration() {
            const payload = {
                accountId: utils.removeLongSuffix(this.account.id),
                body: this.automationMessage.messageContent,
                externalReferenceId: `${this.funnelId}`,
                phoneTypes: this.automationMessage.phoneTypes,
            };

            if (this.automationMessage.id === 0) {
                payload.phoneNumberId = utils.removeLongSuffix(this.account.phoneNumberId);

                try {
                    this.showEndpointError = false;

                    const { data } = await createAutomationMessage(payload);

                    const { id } = data;

                    this.automationMessage.id = id;
                } catch (error) {
                    this.showEndpointError = true;

                    if (error.response?.status === 503) {
                        this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorSavingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.serviceUnavailable')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                    } else {
                        this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorSavingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                        console.error(this.endpointErrorMessages, error);
                    }

                    console.error(this.endpointErrorMessages, error);
                }
            } else {
                payload.automationMessageId = utils.removeLongSuffix(this.automationMessage.id);
                payload.pathList = ['body', 'phoneTypes'];

                try {
                    await updateAutomationMessage(payload);
                } catch (error) {
                    this.showEndpointError = true;

                    if (error.response?.status === 503) {
                        this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorUpdatingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.serviceUnavailable')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                    } else {
                        this.endpointErrorMessages = `${this.$t('ktvThen.KtvEndPointErrors.errorUpdatingMessage')} ${this.$t('ktvThen.KtvEndPointErrors.tryAgainLater')}`;
                        console.error(this.endpointErrorMessages, error);
                    }

                    console.error(this.endpointErrorMessages, error);
                }
            }
        },

        async clickedSaving() {
            if (this.isConfigurationValid) {
                await this.saveConfiguration();

                if (!this.showEndpointError) {
                    const event = {
                        data: {
                            externalId: this.automationMessage.id,
                            error: null,
                        },
                    };

                    this.$emit('update-internal-config', event);
                }
            } else {
                this.showError = true;
            }
        },

        handleSettingButtonClick() {
            if (this.isSpaApp) {
                const settingsUrl = `${this.spaBaseUrl}${TEXT_MESSAGE_VERIFICATION_SETTINGS_SPA}`;

                window.open(settingsUrl, '_blank', 'noopener,noreferrer');
            } else {
                window.open(TEXT_MESSAGE_VERIFICATION_SETTINGS_ULTIMATE, '_blank', 'noopener,noreferrer');
            }
        },

        close() {
            this.$emit('close-modal');
        },
    },
};

</script>

<style scoped lang="scss">

.ktv-then {
    display: flex;
    margin: var(--modal-padding, 1.5rem);
}

.ktv-then-spinner {
    margin-top: $spacing-800;
    display: flex;
    flex-direction: column;
    align-items: center;
    vertical-align: middle;
}

.ktv-then-content {
    margin-top: $spacing-400;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 3;
}

.ktv-then-preview {
    flex: 2;
}

.ktv-component {
    display: flex;
    flex-direction: column;
}

.ktv-card {
    margin-bottom: $spacing-400;
    width: 52%;
}

.ktv-card.error {
    margin-top: $spacing-300;
}

.ktv-footer {
    width: 52%;
    padding-bottom: $spacing-200;
    font-size: $font-size-xs;
    gap: $spacing-100;
    color: $color-gray-700;
    background-color: $color-white;
    display: flex;
    flex-direction: column;
}

.ktv-disabled-alert {
    margin-bottom: $spacing-100;
    width: 52%;
}

.ktv-disabled-number {
    color: $color-ink-900;
    font-family: $font-family;
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    line-height: $line-height-md;
}

.disabled-number {
    color: $color-red-800;
    font-weight: $font-weight-bold;
}

.support-link {
    text-decoration: underline;
}

.go-back-button {
    align-self: flex-start;
    margin-top: $spacing-100;
}

</style>
